"use client"

import { useState, useTransition } from "react"
import { Modal, Text, Stack, Group, NumberInput, Button, Alert } from "@forestsync/ui"
import { TbAlertCircle } from "react-icons/tb"
import { useTranslations } from "next-intl"
import { submitTradeOrder, linkTakeProfitOrder } from "@/actions/trading"
import { TRADE_ORDER_SIDE, TRADE_ORDER_TYPE, TRADE_FILL_TYPE, TRADE_ORDER_TIME_IN_FORCE } from "@forestsync/db"
import type { UserPosition } from "@/actions/trading"

interface SetTPSLModalProps {
  opened: boolean
  position: UserPosition
  creditType: string
  vintage: number
  projectRefId: string
  lastPrice: number | null
  onClose: () => void
  onSuccess?: () => void
}

// SL is intentionally not offered here: it would need a resting SELL STOP order, and STOP
// orders are hard-rejected server-side (order.ts) since nothing monitors live price to trigger
// one — see the CLOB price-monitoring gap tracked for a later phase. Showing SL as "coming soon"
// is more honest than accepting input for an order type that would just fail at submission.
export function SetTPSLModal({
  opened,
  position,
  creditType,
  vintage,
  projectRefId,
  lastPrice,
  onClose,
  onSuccess,
}: SetTPSLModalProps) {
  const t = useTranslations("trading")
  const vol = Math.floor(position.filledVolume)
  const entryPrice = position.price ?? 0.01

  const [tpPrice, setTpPrice] = useState<number>(
    lastPrice ? Math.round(lastPrice * 1.1 * 100) / 100 : Math.round(entryPrice * 1.2 * 100) / 100
  )
  const [tpVol, setTpVol] = useState<number>(vol)

  const [isPending, startTransition] = useTransition()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  function handleSubmit() {
    if (tpPrice <= 0) { setError(t("tpsl.tpPriceError")); return }
    if (tpVol <= 0 || tpVol > vol) { setError(t("tpsl.tpVolumeError")); return }

    setError(null)
    startTransition(async () => {
      const res = await submitTradeOrder({
        creditType,
        vintage,
        projectRefId,
        side: TRADE_ORDER_SIDE.ASK,
        fillType: TRADE_FILL_TYPE.PARTIAL,
        orderTimeInForce: TRADE_ORDER_TIME_IN_FORCE.GTC,
        orderType: TRADE_ORDER_TYPE.LIMIT,
        price: tpPrice,
        volume: tpVol,
      })

      if ("error" in res) {
        setError(res.error)
        return
      }

      // Best-effort linkage — the TP order itself is already live even if this update fails,
      // it just wouldn't show as linked on the Positions row until re-linked.
      if (res.order) {
        await linkTakeProfitOrder(String(res.order.id), String(position.id)).catch(() => {})
      }

      setSuccess(t("tpsl.tpPlaced", { vol: tpVol, price: tpPrice.toFixed(2) }))
      onSuccess?.()
      setTimeout(onClose, 1500)
    })
  }

  const positionValue = vol * entryPrice

  return (
    <Modal opened={opened} onClose={onClose} title={t("tpsl.title")} size="sm" centered>
      <Stack gap="md">
        <Stack gap={4} p="xs" bg="var(--mantine-color-default)" bd="1px solid var(--mantine-color-default-border)">
          <Group justify="space-between">
            <Text size="xs" c="dimmed">{t("tpsl.position")}</Text>
            <Text size="xs" fw={600}>{vol} lots @ €{entryPrice.toFixed(2)}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="xs" c="dimmed">{t("tpsl.entryValue")}</Text>
            <Text size="xs">€{positionValue.toFixed(2)}</Text>
          </Group>
          {lastPrice && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{t("tpsl.lastPrice")}</Text>
              <Text size="xs">€{lastPrice.toFixed(2)}</Text>
            </Group>
          )}
        </Stack>

        {position.tpOrder && (
          <Alert color="teal" variant="light">
            <Text size="xs">{t("tpsl.tpActive", { vol: position.tpOrder.volume, price: position.tpOrder.price.toFixed(2) })}</Text>
          </Alert>
        )}

        <Group align="flex-end" gap="xs">
          <NumberInput
            label={t("tpsl.tpPrice")}
            value={tpPrice}
            onChange={(v) => setTpPrice(typeof v === "number" ? v : tpPrice)}
            min={0.01}
            step={0.01}
            decimalScale={2}
            size="xs"
            flex={1}
          />
          <NumberInput
            label={t("tpsl.lots")}
            value={tpVol}
            onChange={(v) => setTpVol(typeof v === "number" ? Math.min(v, vol) : tpVol)}
            min={1}
            max={vol}
            step={1}
            size="xs"
            w={70}
          />
        </Group>

        {lastPrice && tpPrice > lastPrice && (
          <Text size="xs" c="green">
            {t("tpsl.tpProfit")} +€{((tpPrice - entryPrice) * Math.min(tpVol, vol)).toFixed(2)}
          </Text>
        )}

        <Alert color="gray" variant="light">
          <Text size="xs">{t("tpsl.slComingSoon")}</Text>
        </Alert>

        {error && (
          <Alert icon={<TbAlertCircle />} color="red" variant="light">
            <Text size="xs">{error}</Text>
          </Alert>
        )}

        {success && (
          <Alert color="green" variant="light">
            <Text size="xs">{success}</Text>
          </Alert>
        )}

        <Button onClick={handleSubmit} loading={isPending} fullWidth>
          {t("tpsl.placeOrders")}
        </Button>
      </Stack>
    </Modal>
  )
}
